import { useEffect, useState } from "react";
import { api, formatUSD } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

export const Stat = ({ label, value }) => (
  <div className="surface p-6">
    <div className="overline text-white/40 mb-2">{label}</div>
    <div className="font-mono-brand text-3xl">{value}</div>
  </div>
);

export const PAYMENT_COLORS = {
  "Card": "#C77746",
  "Cash App Pay": "#22C55E",
  "Apple Pay": "#E5E7EB",
  "Google Pay": "#60A5FA",
  "Link": "#A78BFA",
  "Amazon Pay": "#FBBF24",
  "Unknown": "#6B7280",
};

export function Overview() {
  const [a, setA] = useState(null);  useEffect(() => { api.get("/admin/analytics").then(r => setA(r.data)); }, []);
  const fmtDuration = (secs) => {
    if (!secs || secs < 1) return "—";
    const s = Math.round(secs);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60), r = s % 60;
    return r ? `${m}m ${r}s` : `${m}m`;
  };
  if (!a) return <div className="text-white/50">Loading…</div>;  return (
    <div>
      <h1 className="font-serif text-4xl mb-2">Overview</h1>
      <div className="overline text-white/40 mb-10">Sales & visitor performance</div>

      <div className="overline text-white/40 mb-3">Revenue</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Today" value={formatUSD(a.daily)} />
        <Stat label="This Week" value={formatUSD(a.weekly)} />
        <Stat label="This Month" value={formatUSD(a.monthly)} />
        <Stat label="This Year" value={formatUSD(a.yearly)} />
      </div>

      {a.payment_mix && a.payment_mix.length > 0 && (
        <div className="surface p-6 mb-8" data-testid="payment-mix">
          <div className="overline text-white/40 mb-4">Payment Method Mix · All paid orders</div>
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={a.payment_mix} dataKey="count" nameKey="method" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2}>
                    {a.payment_mix.map((e) => <Cell key={e.method} fill={PAYMENT_COLORS[e.method] || "#888"} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#141414", border: "1px solid #222", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {(() => { const total = a.payment_mix.reduce((s, r) => s + r.count, 0);
                return a.payment_mix.map(r => (
                  <div key={r.method} className="flex items-center justify-between text-sm" data-testid={`payment-mix-row-${r.method}`}>
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-sm" style={{ background: PAYMENT_COLORS[r.method] || "#888" }} />
                      <span className="text-white/80">{r.method}</span>
                    </span>
                    <span className="text-white/60 font-mono-brand">{r.count} · {Math.round(r.count / total * 100)}%</span>
                  </div>
                )); })()}
            </div>
          </div>
        </div>
      )}

      <div className="overline text-white/40 mb-3">Visitors</div>      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Stat label="Today" value={`${a.visits_daily} · ${a.unique_daily} uniq`} />
        <Stat label="This Week" value={`${a.visits_weekly} · ${a.unique_weekly} uniq`} />
        <Stat label="This Month" value={`${a.visits_monthly} · ${a.unique_monthly} uniq`} />
        <Stat label="This Year" value={`${a.visits_yearly} · ${a.unique_yearly} uniq`} />
      </div>

      <div className="overline text-white/40 mb-3">Avg Time on Site</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="avg-time-stats">
        <Stat label="Today" value={fmtDuration(a.avg_time_daily)} />
        <Stat label="This Week" value={fmtDuration(a.avg_time_weekly)} />
        <Stat label="This Month" value={fmtDuration(a.avg_time_monthly)} />
        <Stat label="This Year" value={fmtDuration(a.avg_time_yearly)} />
      </div>

      {a.time_by_page && a.time_by_page.length > 0 && (
        <div className="surface p-6 mb-6" data-testid="time-by-page">
          <div className="overline text-white/40 mb-4">Avg Time by Page · Last 30 days</div>
          <table className="w-full text-sm font-mono-brand">
            <thead className="text-white/50 text-xs uppercase">
              <tr>
                <th className="text-left p-2">Path</th>
                <th className="text-right p-2">Avg</th>
                <th className="text-right p-2">Samples</th>
              </tr>
            </thead>
            <tbody>
              {a.time_by_page.map(row => (
                <tr key={row.path} className="border-t border-white/10">
                  <td className="p-2 text-white/80">{row.path}</td>
                  <td className="p-2 text-right">{fmtDuration(row.avg_seconds)}</td>
                  <td className="p-2 text-right text-white/40">{row.samples}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="surface p-6 mb-6">
        <div className="overline text-white/40 mb-6">Last 30 days · Revenue</div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={a.daily_series}>
              <CartesianGrid stroke="#222" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}/>
              <YAxis stroke="#666" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}/>
              <Tooltip contentStyle={{ background: "#141414", border: "1px solid #222", fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Line type="monotone" dataKey="amount" stroke="#fff" strokeWidth={2} dot={{ fill: "#fff", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="surface p-6">
        <div className="overline text-white/40 mb-6">Last 30 days · Visits & Unique Visitors</div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={a.daily_series}>
              <CartesianGrid stroke="#222" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}/>
              <YAxis stroke="#666" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}/>
              <Tooltip contentStyle={{ background: "#141414", border: "1px solid #222", fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Line type="monotone" dataKey="visits" stroke="#C77746" strokeWidth={2} dot={{ fill: "#C77746", r: 3 }} name="Visits"/>
              <Line type="monotone" dataKey="unique_visitors" stroke="#22C55E" strokeWidth={2} dot={{ fill: "#22C55E", r: 3 }} name="Unique"/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

